import { useCallback, useEffect, useRef } from 'react';

/**
 * How long a freshly mounted question panel ignores clicks (ms). A single-choice answer advances at once and the
 * next panel mounts under the same pointer, so the second half of a double-click would answer it too.
 */
export const PANEL_CLICK_GUARD_MS = 300;

/** Wraps a handler so it does nothing until the guard window has passed. */
export type ClickGuard = <A extends unknown[]>(handler: (...args: A) => void) => (...args: A) => void;

/**
 * Returns a `guard` that swallows calls during the first `delayMs` after the component mounts. Mount the panel
 * afresh for each step (key it) and the window restarts with it. A delay of 0 or less guards nothing (tests).
 * The window is tracked in a ref: ignoring a click never re-renders and the buttons are not `disabled`, so
 * keyboard focus and the card's look are untouched.
 */
export function useClickGuard(delayMs: number = PANEL_CLICK_GUARD_MS): ClickGuard {
  const open = useRef(delayMs <= 0);
  useEffect(() => {
    if (delayMs <= 0) {
      open.current = true;
      return;
    }
    open.current = false;
    const timer = window.setTimeout(() => {
      open.current = true;
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs]);

  return useCallback<ClickGuard>(
    (handler) =>
      (...args) => {
        if (open.current) handler(...args);
      },
    [],
  );
}
